/**
 * Composant Header
 *
 * En-tête du dashboard affichant :
 * - Nom du client et du projet
 * - Dernière mise à jour avec temps écoulé
 * - Bouton de rafraîchissement manuel
 * - Menu d'export (PDF, Excel, CSV)
 * - Bouton de déconnexion
 * - Sélecteur de projet (si plusieurs projets)
 *
 * Design moderne avec icônes lucide-react
 *
 * @param {string} clientName - Nom du client
 * @param {string} projectName - Nom du projet
 * @param {string} lastUpdateText - Texte de la dernière mise à jour
 * @param {Function} onRefresh - Callback pour le rafraîchissement manuel
 * @param {Function} onExport - Callback pour l'export (type: 'pdf' | 'excel' | 'csv')
 * @param {Function} onLogout - Callback pour la déconnexion
 * @param {Array} projects - Liste des projets disponibles (optionnel)
 * @param {number} selectedProjectId - ID du projet sélectionné (optionnel)
 * @param {Function} onProjectChange - Callback lors du changement de projet (optionnel)
 */

import React, { useState, useRef, useEffect } from 'react';
import ThemeToggle from '../common/ThemeToggle';
import {
  RefreshCw,
  Download,
  ChevronDown,
  FileText,
  Table,
  File
} from 'lucide-react';

const Header = ({
  clientName = 'Votre organisation',
  projectName = 'Projet',
  lastUpdateText = '',
  onRefresh,
  onExport,
  onLogout,
  projects = [],
  selectedProjectId,
  onProjectChange,
  isExporting = false,
}) => {
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const exportMenuRef = useRef(null);

  // Options d'export
  const exportOptions = [
    { type: 'pdf', label: 'Exporter en PDF', icon: FileText, color: 'text-red-600' },
    { type: 'excel', label: 'Exporter en Excel', icon: Table, color: 'text-green-600' },
    { type: 'csv', label: 'Exporter en CSV', icon: File, color: 'text-brand-teal-500' },
  ];

  // Fermer les menus lors du clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setIsExportMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gérer le rafraîchissement avec animation
  const handleRefresh = async () => {
    if (onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      await onRefresh();
      // Animation minimale de 500ms
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  // Gérer l'export
  const handleExport = (type) => {
    if (onExport) {
      onExport(type);
    }
    setIsExportMenuOpen(false);
  };

  return (
    <header className="horizon-header">
      <div className="horizon-header-row">
        {/* Partie gauche : Informations du projet */}
        <div className="flex items-center gap-6">
          {/* Nom du projet */}
          <div>
            <p className="horizon-breadcrumb">Vue d’ensemble</p>
            <h1>{projectName}</h1>
            <p className="horizon-subtitle">{lastUpdateText ? `Dernière mise à jour ${lastUpdateText}` : 'Vos User Stories, de la demande à la livraison.'}</p>
          </div>
        </div>

        {/* Partie droite : Actions */}
        <div className="horizon-header-actions">
          <ThemeToggle />
          {/* Bouton Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2.5 bg-white hover:bg-gray-50 rounded-xl transition-all duration-200
                     border border-gray-300 shadow-sm hover:shadow-md
                     disabled:opacity-50 disabled:cursor-not-allowed
                     focus:outline-none focus:ring-2 focus:ring-[#6B4C9A]"
            title="Rafraîchir les données"
            aria-label="Rafraîchir les données"
          >
            <RefreshCw
              className={`w-5 h-5 text-[#6B4C9A] ${isRefreshing ? 'animate-spin' : ''}`}
            />
          </button>

          {/* Bouton Notifications - Masqué pour le moment */}
          {/* <button
            className="p-2.5 bg-white hover:bg-gray-50 rounded-xl transition-all duration-200
                     border border-gray-200 shadow-sm hover:shadow-md relative
                     focus:outline-none focus:ring-2 focus:ring-brand-purple-400"
            title="Notifications"
          >
            <Bell className="w-5 h-5 text-gray-600" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
          </button> */}

          {/* Menu Export */}
          {onExport && (
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                disabled={isExporting}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#6B4C9A]
                         hover:bg-[#59407F] text-white rounded-xl transition-all duration-200
                         shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#8768B5]
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
                <span className="text-sm font-semibold">
                  {isExporting ? 'Export...' : 'Exporter'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {/* Menu déroulant d'export */}
              {isExportMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl
                              border border-gray-200 py-2 z-50">
                  {exportOptions.map((option) => (
                    <button
                      key={option.type}
                      onClick={() => handleExport(option.type)}
                      className="w-full px-4 py-2.5 text-left flex items-center gap-3
                               hover:bg-gray-50 transition-all duration-150 rounded-lg mx-1"
                    >
                      <option.icon className={`w-5 h-5 ${option.color}`} />
                      <span className="text-sm text-gray-700 font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
